import { useEffect, useState, type ComponentProps } from 'react';
import { Authenticated, Refine } from '@refinedev/core';
import routerProvider, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from '@refinedev/react-router';
import { Navigate, Outlet, Route, Routes } from 'react-router';

import { authProvider } from '@/lib/auth-provider';
import { RequireAdmin } from '@/lib/require-admin';
import { dataProvider } from '@/lib/data-provider';
import { getBootstrapStatus, type BootstrapStatus } from '@/lib/bootstrap-status';
import { resources } from '@/resources';
import { AdminLayout } from '@/components/AdminLayout';
import { HubLayout } from '@/components/HubLayout';
import { Login } from '@/pages/auth/Login';
import { Bootstrap } from '@/pages/auth/Bootstrap';
import { InvitePage } from '@/pages/auth/Invite';
import { MfaPage } from '@/pages/auth/Mfa';
import { ForgotPassword } from '@/pages/auth/ForgotPassword';
import { ResetPasswordPage } from '@/pages/auth/ResetPassword';
import { Account } from '@/pages/account/Account';
import { Dashboard } from '@/pages/dashboard/Dashboard';
import { TracksHome } from '@/pages/home/TracksHome';
import { Integrations } from '@/pages/integrations/Integrations';
import { Settings } from '@/pages/settings/Settings';
import { UsersList } from '@/pages/users/List';
import { UsersShow } from '@/pages/users/Show';
import { UsersEdit } from '@/pages/users/Edit';
import { ApiKeysList } from '@/pages/api-keys/List';
import { ApiKeysCreate } from '@/pages/api-keys/Create';
import { RelationsList } from '@/pages/relations/List';
import { RelationsCreate } from '@/pages/relations/Create';
import { ModelsList } from '@/pages/models/List';
import { ModelsCreate } from '@/pages/models/Create';
import { ModelsEdit } from '@/pages/models/Edit';
import { Assignment } from '@/pages/models/Assignment';
import { IdentityProviders } from '@/pages/identity-providers/IdentityProviders';
import { WebResearch } from '@/pages/web-research/WebResearch';
import { McpServers } from '@/pages/mcp-servers/McpServers';
import { DbConnectorsList } from '@/pages/db-connectors/List';
import { DbConnectorsCreate } from '@/pages/db-connectors/Create';
import { DbConnectorsShow } from '@/pages/db-connectors/Show';
import { DocumentsList } from '@/pages/documents/List';
import { DocumentsShow } from '@/pages/documents/Show';
import { DocumentsText } from '@/pages/documents/Text';
import { TracksList } from '@/pages/tracks/List';
import { TracksShow } from '@/pages/tracks/Show';
import { TracksCreate } from '@/pages/tracks/Create';
import { TracksEdit } from '@/pages/tracks/Edit';
import { PromptsList } from '@/pages/prompts/List';
import { PromptsShow } from '@/pages/prompts/Show';
import { PromptsCreate } from '@/pages/prompts/Create';
import { PromptsEdit } from '@/pages/prompts/Edit';
import { ApprovalsList } from '@/pages/approvals/List';
import { ApprovalsShow } from '@/pages/approvals/Show';
import { SchedulesList } from '@/pages/schedules/List';
import { SessionsShow } from '@/pages/sessions/Show';
import { AuditLog } from '@/pages/audit/AuditLog';
import { Usage } from '@/pages/usage/Usage';
import { ExtensionAdminRoute } from '@/extensions/ExtensionAdminRoute';

type DocumentTitleOptions = Parameters<
  NonNullable<ComponentProps<typeof DocumentTitleHandler>['handler']>
>[0];

const APP_TITLE = 'Trackframe Hub';

function documentTitleHandler({ pathname, autoGeneratedTitle }: DocumentTitleOptions): string {
  if (pathname === '/login') return `Sign in | ${APP_TITLE}`;
  if (pathname === '/bootstrap') return `Bootstrap Admin | ${APP_TITLE}`;
  if (pathname === '/') return APP_TITLE;
  if (autoGeneratedTitle === 'Refine') return APP_TITLE;
  return autoGeneratedTitle.replace(/ \| Refine$/, ` | ${APP_TITLE}`);
}

function useBootstrapStatus(): BootstrapStatus | null {
  const [status, setStatus] = useState<BootstrapStatus | null>(null);
  useEffect(() => {
    let cancelled = false;
    getBootstrapStatus().then((s) => {
      if (!cancelled) setStatus(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return status;
}

export function App() {
  const status = useBootstrapStatus();
  // While the probe is in flight render nothing — both Login and Bootstrap
  // need to know which one to redirect to, and the result lands within ~10ms.
  const showBootstrap = status?.enabled && !status?.hasAdmin;

  return (
    <Refine
      dataProvider={dataProvider}
      authProvider={authProvider}
      routerProvider={routerProvider}
      resources={resources}
      options={{ syncWithLocation: true, warnWhenUnsavedChanges: true }}
    >
      <Routes>
        <Route
          path="/login"
          element={status === null ? null : showBootstrap ? <Navigate to="/bootstrap" replace /> : <Login />}
        />
        <Route
          path="/bootstrap"
          element={status === null ? null : status.enabled ? <Bootstrap /> : <Navigate to="/login" replace />}
        />
        {/* HUB-106: set-password page an invite email links to. Public on
            both sides — the hub's auth hook bypasses /login/*, and the auth
            provider treats /login/* as a public path. */}
        <Route path="/login/invite" element={<InvitePage />} />
        {/* Self-service reset: request a mail, then set the password from its
            link. Same public-by-prefix rule as /login/invite. */}
        <Route path="/login/forgot" element={<ForgotPassword />} />
        <Route path="/login/reset" element={<ResetPasswordPage />} />
        {/* HUB-121: the second step of a sign-in (code, or forced enrolment).
            Reached from Login with the challenge in router state, or from the
            OAuth callback with it in the tf_mfa cookie. Public by prefix. */}
        <Route path="/login/mfa" element={<MfaPage />} />

        {/* Hub root: track list with the shared header. Auth-gated; the
            auth provider's check() bounces unauthenticated users to
            /login?return_to=<current path>. */}
        <Route
          element={
            <Authenticated key="hub" fallback={<CatchAllNavigate to="/login" />}>
              <HubLayout>
                <Outlet />
              </HubLayout>
            </Authenticated>
          }
        >
          <Route path="/" element={<TracksHome />} />
          {/* M28 — the M16 CONN-08 deep link is now canonicalized under the
              /me/* workspace (COP-117 D-13). Keep the old path working by
              redirecting it to /me/integrations. */}
          <Route path="/settings/integrations" element={<Navigate to="/me/integrations" replace />} />
        </Route>

        {/* Admin subtree, sharing the same auth gate but a different chrome
            (sidebar nav, refine resources). All resources live under
            /admin/* — see resources.ts for the matching link paths. */}
        <Route
          element={
            <Authenticated key="admin" fallback={<CatchAllNavigate to="/login" />}>
              <RequireAdmin>
                <AdminLayout>
                  <Outlet />
                </AdminLayout>
              </RequireAdmin>
            </Authenticated>
          }
        >
          <Route path="/admin" element={<Dashboard />} />

          <Route path="/admin/users">
            <Route index element={<UsersList />} />
            <Route path="show/:id" element={<UsersShow />} />
            {/* Same Show.tsx renders for the memory sub-path; the page
                reads the URL to pick the active tab (plan-9, D-20). */}
            <Route path="show/:id/memory" element={<UsersShow />} />
            {/* HUB-109 D7: the user's tracks (level + roles). */}
            <Route path="show/:id/tracks" element={<UsersShow />} />
            <Route path="edit/:id" element={<UsersEdit />} />
          </Route>

          <Route path="/admin/api-keys">
            <Route index element={<ApiKeysList />} />
            <Route path="create" element={<ApiKeysCreate />} />
          </Route>

          {/* M27 — UI renamed Relations → Permissions (ADM-10). The pages,
              components, and client methods keep their `relations` names; only
              the route/resource/label move (§4 D-11). */}
          <Route path="/admin/permissions">
            <Route index element={<RelationsList />} />
            <Route path="create" element={<RelationsCreate />} />
          </Route>

          {/* M27 — LLM model registry + per-purpose assignment (ADM-01/03;
              HUB-082). */}
          <Route path="/admin/models">
            <Route index element={<ModelsList />} />
            <Route path="create" element={<ModelsCreate />} />
            <Route path="edit/:id" element={<ModelsEdit />} />
          </Route>
          <Route path="/admin/assignments" element={<Assignment />} />

          {/* M27 — DB-backed login-SSO + Drive-connector OAuth config, and the
              web-research provider config (ADM-07/15; HUB-083/084). */}
          <Route path="/admin/identity-providers" element={<IdentityProviders />} />
          <Route path="/admin/web-research" element={<WebResearch />} />
          {/* HUB-107 — MCP server registry (admin). */}
          <Route path="/admin/mcp-servers" element={<McpServers />} />

          {/* M24 — DB connectors (DBC-01). List/Create are admin-only; the
              Show route lives in its own auth-only block below (Integrations
              precedent) so editor-granted non-admins can deep-link to it. */}
          <Route path="/admin/db-connectors">
            <Route index element={<DbConnectorsList />} />
            <Route path="create" element={<DbConnectorsCreate />} />
          </Route>

          {/* M28 — cross-track document master/detail + hybrid search
              (ADM-12/13; COP-117 D-14). The SAME DocumentsList/DocumentsShow
              source mounts at /me/documents in task-06 — this is not a fork. */}
          <Route path="/admin/documents">
            <Route index element={<DocumentsList />} />
            <Route path="show/:id" element={<DocumentsShow />} />
            <Route path="show/:id/text" element={<DocumentsText />} />
          </Route>

          <Route path="/admin/tracks">
            <Route index element={<TracksList />} />
            <Route path="show/:id" element={<TracksShow />} />
            {/* Same Show.tsx renders for the tab sub-path (overview | access |
                roles | settings); the page reads the URL to pick the active
                tab (HUB-109 / TAZ-07). */}
            <Route path="show/:id/:tab" element={<TracksShow />} />
            <Route path="create" element={<TracksCreate />} />
            <Route path="edit/:id" element={<TracksEdit />} />
          </Route>

          <Route path="/admin/prompts">
            <Route index element={<PromptsList />} />
            <Route path="show/:id" element={<PromptsShow />} />
            <Route path="create" element={<PromptsCreate />} />
            <Route path="edit/:id" element={<PromptsEdit />} />
          </Route>

          {/* APPR-07 / D-16 — admin approvals queue. Show takes `?userId=`
              forwarded by the list view so the admin route can scope the
              read under withUserRole(targetUserId) per HUB-024 §6. */}
          <Route path="/admin/approvals">
            <Route index element={<ApprovalsList />} />
            <Route path="show/:id" element={<ApprovalsShow />} />
          </Route>

          {/* M33 task-06 — the bounded adoption surface (ATRU-06; COP-134 D4).
              ADMIN-ONLY on purpose: GET /usage/summary gates on requireAdmin
              (whole-deployment totals are a wider disclosure than a scoped
              audit read), so the page lives inside this RequireAdmin subtree
              rather than in one of the auth-only blocks below. */}
          <Route path="/admin/usage" element={<Usage />} />

          <Route path="/admin/settings" element={<Settings />} />

          {/* HUB-103 — extension admin pages + declared-settings page, bundled
              at build time (src/extensions/registry.ts glob) and reachable only
              for extensions the hub reports enabled (GET /extensions). Splat:
              the extension may nest its own <Routes>. */}
          <Route path="/admin/ext/:name/*" element={<ExtensionAdminRoute />} />

          <Route path="/admin/*" element={<NavigateToResource />} />
        </Route>

        {/* M24 — DB-connector Show / schema editor (DBC-03/04; HUB-079 §4).
            Like the M16 /settings/integrations precedent above, this route is
            auth-gated only and deliberately NOT under RequireAdmin: Zanzibar
            `editor`-granted non-admins reach the schema editor by deep link.
            The hub enforces per-connector access (task-05 gate tiers); the
            page renders the no-access state for everyone else. */}
        <Route
          element={
            <Authenticated key="db-connectors" fallback={<CatchAllNavigate to="/login" />}>
              <AdminLayout>
                <Outlet />
              </AdminLayout>
            </Authenticated>
          }
        >
          <Route path="/admin/db-connectors/:id" element={<DbConnectorsShow />} />
        </Route>

        {/* M33 task-04b — read-only session viewer (COP-134 D1 / ATRU-04). The
            destination for the run-history Session cell: a scheduled run's
            output has to be readable, not merely referenced.

            Like the /admin/db-connectors/:id precedent directly above, this
            block is auth-gated ONLY and deliberately NOT under RequireAdmin.
            The hub's `session#read = owner ∪ parent.track#admin` tuple check on
            GET /sessions/:id/messages is the real gate, and it is stricter than
            a role check in both directions: a schedule owner who is not a hub
            admin MUST be able to open their own run's session, and a hub admin
            with no `track#admin` tuple must NOT read someone else's. The page
            renders the no-access state (403) for everyone else.

            `show/:id` deep link only — no index, no search, no user picker. Like
            /admin/schedules below, no cross-owner browser is introduced
            (HUB-024, COP-074/COP-075). */}
        <Route
          element={
            <Authenticated key="sessions" fallback={<CatchAllNavigate to="/login" />}>
              <AdminLayout>
                <Outlet />
              </AdminLayout>
            </Authenticated>
          }
        >
          <Route path="/admin/sessions/show/:id" element={<SessionsShow />} />
        </Route>

        {/* M28 — non-admin user workspace (ADM-11; COP-117 D-13/D-14). Like the
            /settings/integrations and /admin/db-connectors/:id precedents above,
            this block is auth-gated ONLY and deliberately NOT under RequireAdmin:
            every signed-in user reaches their Documents + Integrations inside the
            SAME AdminLayout shell (the role-aware Sidebar trims the admin sections
            for non-admins). The data stays protected by the hub's per-route
            requireAdmin / per-document read gates; this is chrome. /me/documents
            mounts the SAME DocumentsList as /admin/documents — one component, no
            fork (D-14). */}
        <Route
          element={
            <Authenticated key="me" fallback={<CatchAllNavigate to="/login" />}>
              <AdminLayout>
                <Outlet />
              </AdminLayout>
            </Authenticated>
          }
        >
          <Route path="/me/documents" element={<DocumentsList />} />
          {/* Same DocumentsShow as /admin/documents/show/:id — one component, no
              fork (D-14). The hub `read`-gates each document, so a non-admin can
              inspect exactly what they may read from their own workspace. */}
          <Route path="/me/documents/show/:id" element={<DocumentsShow />} />
          <Route path="/me/documents/show/:id/text" element={<DocumentsText />} />
          <Route path="/me/integrations" element={<Integrations />} />
          <Route path="/me/account" element={<Account />} />
          {/* S08 — owner scheduled actions surface. Calls owner-scoped
              /schedules, /notifications and /executions routes directly; no
              cross-owner admin browser is introduced (HUB-024,
              COP-074/COP-075). Open to every signed-in user (owner decision
              2026-10-10, pinned in app-route-gates.test.ts). The path stays
              under /admin because the Copilot's schedule card links here. */}
          <Route path="/admin/schedules">
            <Route index element={<SchedulesList />} />
          </Route>
          {/* HUB-115 — audit log. Auth-only like /admin/schedules, NOT under
              RequireAdmin: GET /audit/events self-scopes a caller to their own
              rows unless they hold audit_log:global#viewer or are an admin
              (HUB-063 §4), so a non-admin who opens it sees exactly their own
              actions. The nav entry is admin-section only (owner decision
              2026-10-16; pinned in app-route-gates.test.ts). */}
          <Route path="/admin/audit" element={<AuditLog />} />
        </Route>

        {/* Anything else (typo'd top-level path) falls back to the hub root. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <UnsavedChangesNotifier />
      <DocumentTitleHandler handler={documentTitleHandler} />
    </Refine>
  );
}
